/**
 * The label layouts OptiFlow prints. A small fixed set, never a designer: two thermal rolls, one label
 * per page, and two A4 sheets of adhesive labels for an office printer. Every size is in millimetres,
 * because that is what the printer and the label stock are specified in.
 */
export type LabelPresetId = 'THERMAL_50x25' | 'THERMAL_40x30' | 'A4_3x8' | 'A4_5x13';

export interface LabelPreset {
  id: LabelPresetId;
  kind: 'thermal' | 'sheet';
  page: { width: number; height: number };
  label: { width: number; height: number };
  columns: number;
  rows: number;
  /** Where the grid starts on the page: the sheet's own top and left margins. */
  margin: { top: number; left: number };
}

export const LABEL_PRESETS: Record<LabelPresetId, LabelPreset> = {
  THERMAL_50x25: {
    id: 'THERMAL_50x25',
    kind: 'thermal',
    page: { width: 50, height: 25 },
    label: { width: 50, height: 25 },
    columns: 1,
    rows: 1,
    margin: { top: 0, left: 0 },
  },
  THERMAL_40x30: {
    id: 'THERMAL_40x30',
    kind: 'thermal',
    page: { width: 40, height: 30 },
    label: { width: 40, height: 30 },
    columns: 1,
    rows: 1,
    margin: { top: 0, left: 0 },
  },
  A4_3x8: {
    id: 'A4_3x8',
    kind: 'sheet',
    page: { width: 210, height: 297 },
    label: { width: 70, height: 37 },
    columns: 3,
    rows: 8,
    margin: { top: 0.5, left: 0 },
  },
  A4_5x13: {
    id: 'A4_5x13',
    kind: 'sheet',
    page: { width: 210, height: 297 },
    label: { width: 38, height: 21 },
    columns: 5,
    rows: 13,
    margin: { top: 12, left: 10 },
  },
};

export const LABEL_PRESET_ORDER: LabelPresetId[] = [
  'THERMAL_50x25',
  'THERMAL_40x30',
  'A4_3x8',
  'A4_5x13',
];

/** Labels per printed page. */
export function labelsPerPage(preset: LabelPreset): number {
  return preset.columns * preset.rows;
}

/** The page rule the printer obeys: the page is the label roll's size or A4, with no browser margin. */
export function pageRule(preset: LabelPreset): string {
  return `@page { size: ${preset.page.width}mm ${preset.page.height}mm; margin: 0; }`;
}
